import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import Seo, { SITE_URL } from "@/lib/seo";
import ShareSheet from "@/components/ShareSheet";
import { SpotifyLogo, AppleLogo, Bell, Check } from "@phosphor-icons/react";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

function useCountdown(dateStr) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const target = new Date(`${dateStr}T00:00:00`).getTime();
  const diff = target - now;
  return { out: diff <= 0, d: Math.floor(diff / 86400000), h: Math.floor(diff / 3600000) % 24, m: Math.floor(diff / 60000) % 60, s: Math.floor(diff / 1000) % 60 };
}

export default function Presave() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/presave/${slug}`).then(({ data }) => setR(data)).catch(() => setErr(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/presave/${slug}/subscribe`, { email });
      setR((cur) => ({ ...cur, fan_count: data.fan_count }));
      setDone(true);
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  if (err) return <Shell><div className="text-center py-40 text-zinc-500 text-xs tracking-widest" data-testid="presave-not-found">RELEASE NOT FOUND</div></Shell>;
  if (!r) return <Shell><div className="text-center py-40 text-zinc-500 text-xs tracking-widest">LOADING...</div></Shell>;
  const cover = `${API.replace(/\/api$/, "")}${r.cover_url}`;
  const album = { "@type": "MusicAlbum", name: r.title, byArtist: { "@type": "MusicGroup", name: r.artist_name }, image: cover, datePublished: r.release_date, genre: r.genre,
    albumReleaseType: r.release_type === "album" ? "AlbumRelease" : r.release_type === "ep" ? "EPRelease" : "SingleRelease", url: `${SITE_URL}/presave/${slug}`,
    recordLabel: { "@id": `${SITE_URL}/#organization` }, numTracks: r.tracks?.length || 1,
    track: (r.tracks || []).map((t) => ({ "@type": "MusicRecording", name: t.title, position: t.track_number, byArtist: { "@type": "MusicGroup", name: r.artist_name } })) };
  return <Shell>
    <Seo title={`${r.title} by ${r.artist_name} — Pre-save`} path={`/presave/${slug}`} image={`${API}/presave/${slug}/card.png?format=square`} imageSize={[1080, 1080]} type="music.album"
      description={`Pre-save "${r.title}" by ${r.artist_name}, ${r.release_type || "single"} out ${r.release_date} on Spotify, Apple Music and 150+ platforms via Asylum Entertainment.`} jsonLd={[album]} />
    <Body r={r} email={email} setEmail={setEmail} done={done} busy={busy} submit={submit} />
  </Shell>;
}

function Body({ r, email, setEmail, done, busy, submit }) {
  const c = useCountdown(r.release_date);
  const spotify = r.spotify_url || r.artist_spotify_url;
  const apple = r.apple_url || r.artist_apple_url;
  const cover = `${API.replace(/\/api$/, "")}${r.cover_url}`;

  return (
    <div className="relative min-h-screen">
      <div className="absolute inset-0 overflow-hidden">
        <img src={cover} alt="" className="w-full h-full object-cover opacity-20 blur-3xl scale-110" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
      </div>
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center" data-testid="presave-page">
        <div className="lg:col-span-5">
          <div className="relative border border-zinc-800">
            <img src={cover} alt={r.title} className="w-full aspect-square object-cover" data-testid="presave-cover" />
            <div className="absolute top-3 left-3 bg-red-600 text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1">{c.out ? "Out Now" : "Coming Soon"}</div>
          </div>
          <div className="mt-3"><ShareSheet slug={r.presave_slug} title={r.title} artist={r.artist_name} /></div>
        </div>
        <div className="lg:col-span-7">
          <div className="w-16 h-1.5 bg-red-600 mb-6" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{r.release_type} · {r.genre}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl uppercase tracking-tighter leading-[0.9] mb-2" data-testid="presave-title">{r.title}</h1>
          <div className="text-lg text-zinc-300 mb-8" data-testid="presave-artist">{r.artist_name}</div>

          {!c.out ? (
            <div className="grid grid-cols-4 border border-zinc-800 mb-8 max-w-md" data-testid="presave-countdown">
              {[["Days", c.d], ["Hrs", c.h], ["Min", c.m], ["Sec", c.s]].map(([l, v]) => (
                <div key={l} className="p-4 border-r border-zinc-800 last:border-r-0 text-center">
                  <div className="font-display text-3xl lg:text-4xl tracking-tighter">{pad(v)}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{l}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs tracking-[0.2em] uppercase text-green-500 mb-8">● Released {r.release_date}</div>
          )}

          {r.tracks?.length > 1 && (
            <div className="mb-8 max-w-md divide-y divide-zinc-800 border border-zinc-800" data-testid="presave-tracklist">
              {r.tracks.map(t => (
                <div key={t.track_number} className="flex gap-3 px-4 py-2 text-sm"><span className="font-display text-zinc-600">{String(t.track_number).padStart(2, "0")}</span><span>{t.title}</span></div>
              ))}
            </div>
          )}

          <div className="max-w-md">
            {done ? (
              <div className="border border-green-600 bg-green-600/10 p-5 flex items-center gap-3" data-testid="presave-success">
                <Check size={20} weight="bold" className="text-green-500" />
                <div><div className="text-sm font-medium">You're on the list.</div><div className="text-xs text-zinc-400">We'll hit your inbox the moment it drops.</div></div>
              </div>
            ) : (
              <form onSubmit={submit} className="flex border border-zinc-700 focus-within:border-red-600 transition-colors" data-testid="presave-form">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="presave-email"
                  className="flex-1 bg-transparent px-4 py-4 text-sm focus:outline-none" />
                <button disabled={busy} data-testid="presave-submit" className="bg-red-600 text-white px-6 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 btn-brutal btn-brutal-red">
                  <Bell size={14} weight="bold" /> {c.out ? "Get Updates" : "Pre-Save"}
                </button>
              </form>
            )}
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-3" data-testid="presave-fan-count">{r.fan_count} fan{r.fan_count === 1 ? "" : "s"} waiting</div>
          </div>

          {(spotify || apple) && (
            <div className="flex flex-wrap gap-3 mt-8">
              {spotify && <a href={spotify} target="_blank" rel="noreferrer" data-testid="presave-spotify" className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-3 text-xs tracking-[0.2em] uppercase hover:border-green-500 hover:text-green-500 transition-colors"><SpotifyLogo size={16} weight="fill" /> Spotify</a>}
              {apple && <a href={apple} target="_blank" rel="noreferrer" data-testid="presave-apple" className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-3 text-xs tracking-[0.2em] uppercase hover:border-white transition-colors"><AppleLogo size={16} weight="fill" /> Apple Music</a>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black">
      <div className="border-b border-zinc-800 px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="presave-logo">
          <span className="w-2 h-2 bg-red-600" /><span className="font-display text-xl tracking-tight uppercase">Asylum<span className="text-red-600">.</span></span>
        </Link>
        <Link to="/pricing" className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white">Release your music →</Link>
      </div>
      {children}
    </div>
  );
}
